import { useEffect, useRef, useState } from "react";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";
import { ProgressRow } from "@/components/viz/ProgressRow";

export default function SocialGeoPage() {
  const [labels, setLabels] = useState<any[]>([]);
  const [byProv, setByProv] = useState<any[]>([]);
  const [unmapped, setUnmapped] = useState<any[]>([]);
  const [svg, setSvg] = useState("");
  const [hover, setHover] = useState<{ name: string; count: number; x: number; y: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin (H-1 WIB)
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);

  useEffect(() => { fetch("/map_indonesia.svg").then((r) => r.text()).then(setSvg); }, []);
  async function load(s2?: string, e2?: string) {
    const r = await fetch(`/api/v1/social/geo?start=${s2 ?? start}&end=${e2 ?? end}`);
    if (r.status === 401) { location.href = "/login"; return; }
    const j = await r.json();
    setLabels(j.labels ?? []); setByProv(j.byProvince ?? []); setUnmapped(j.unmapped ?? []);
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const el = wrapRef.current; if (!el || !svg) return;
    const max = byProv[0]?.count ?? 0;
    const countOf = new Map(byProv.map((p: any) => [p.code, p.count]));
    const nameOf = new Map(byProv.map((p: any) => [p.code, p.name]));
    el.querySelectorAll<SVGPathElement>("g#features path[id^='ID']").forEach((path) => {
      const c = countOf.get(path.id) ?? 0;
      const t = max ? c / max : 0;
      path.style.fill = c === 0 ? "#EEF1F6" : `rgba(235,104,52,${0.26 + t * 0.74})`; // ramp oranye = kanal sosial (bedakan dari peta media massa)
      path.style.cursor = "default";
      path.onmousemove = (ev: MouseEvent) => {
        const r = el.getBoundingClientRect();
        setHover({ name: (nameOf.get(path.id) ?? path.getAttribute("name") ?? path.id) as string, count: c, x: ev.clientX - r.left + 12, y: ev.clientY - r.top + 12 });
      };
      path.onmouseleave = () => setHover(null);
    });
  }, [svg, byProv]);

  const maxL = labels[0]?.count ?? 1;
  const provSum = byProv.reduce((a: number, p: any) => a + p.count, 0);
  const labSum = labels.reduce((a: number, l: any) => a + l.count, 0);
  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Sebaran Lokasi</h1>
          <div className="avio-t-sm avio-muted">Social · label lokasi AI Neticle · {start} → {end} (WIB)</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar wing="social" />

      <div className="avio-tiles">
        <Tile c={4} icon="fa-solid fa-map-location-dot" label="Mention berlabel lokasi" value={labSum} sub="dari label AI vendor" />
        <Tile c={1} icon="fa-solid fa-map" label="Provinsi terjangkau" value={byProv.length} sub="dari 38 provinsi" />
        <Tile c={5} icon="fa-solid fa-tags" label="Label lokasi unik" value={labels.length} sub="nama tempat" />
      </div>

      <div className="avio-grid">
        <div data-col="7">
          <Panel icon="fa-solid fa-map-location-dot" title="Peta sebaran" hint="warna oranye = kanal sosial">
            <div ref={wrapRef} style={{ position: "relative" }}>
              <div dangerouslySetInnerHTML={{ __html: svg }} />
              {hover && (
                <div style={{ position: "absolute", left: hover.x, top: hover.y, background: "var(--gray-900, #10141C)", color: "#fff", padding: "var(--space-4) var(--space-5)", borderRadius: "var(--radius-sm)", fontSize: "var(--text-sm)", pointerEvents: "none", whiteSpace: "nowrap", zIndex: 10 }}>
                  <b>{hover.name}</b> · {hover.count.toLocaleString("id-ID")} mention
                </div>
              )}
            </div>
          </Panel>
        </div>
        <div data-col="5">
          <Panel icon="fa-solid fa-ranking-star" title="Provinsi teratas">
            {byProv.length === 0
              ? <div className="avio-empty"><i className="fa-regular fa-map avio-empty__icon" /><div className="avio-empty__title">Belum ada label lokasi.</div><div className="avio-empty__hint">Jalankan sync Social lebih dulu.</div></div>
              : <div className="avio-plist">
                  {byProv.slice(0, 10).map((p2: any) => (
                    <ProgressRow key={p2.code} name={p2.name} total={p2.count}
                      share={provSum ? (p2.count / provSum) * 100 : 0}
                      maxShare={provSum ? ((byProv[0]?.count ?? 0) / provSum) * 100 : 0}
                      segs={[{ value: p2.count, color: "var(--mark-c2)", title: `${p2.name}: ${p2.count}` }]} />
                  ))}
                </div>}
          </Panel>
        </div>
        <div data-col="7">
          <Panel icon="fa-solid fa-tags" title="Label lokasi" hint="klik untuk melihat post-nya">
            <div className="avio-plist" style={{ maxHeight: 460, overflowY: "auto" }}>
              {labels.map((l: any) => (
                <ProgressRow key={l.id} name={l.label} total={l.count}
                  share={labSum ? (l.count / labSum) * 100 : 0}
                  maxShare={labSum ? (maxL / labSum) * 100 : 0}
                  href={`/a/social/posts?start=${start}&end=${end}&label=${l.id}&labelName=${encodeURIComponent(l.label)}`}
                  segs={[{ value: l.count, color: "var(--mark-c2)", title: `${l.label}: ${l.count}` }]} />
              ))}
              {labels.length === 0 && <div className="avio-empty"><i className="fa-regular fa-map avio-empty__icon" /><div className="avio-empty__title">Belum ada label lokasi.</div></div>}
            </div>
          </Panel>
        </div>
        {unmapped.length > 0 && (
          <div data-col="5">
            <Panel icon="fa-solid fa-triangle-exclamation" title="Belum terpetakan" hint={`${unmapped.length} label`}>
              <div className="avio-t-sm avio-muted" style={{ marginBottom: "var(--space-5)" }}>
                Label ini belum dikenali pemetaan provinsi. Ditampilkan apa adanya, tidak dibuang.
              </div>
              <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
                {unmapped.map((x: any) => (
                  <span key={x.id} className="avio-chip" data-tone="soft"><span className="avio-chip__label">{x.label} · {x.count}</span></span>
                ))}
              </div>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}
